import React from 'react';
import { Prospect } from '../../shared/types';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { MessageSquare, ArrowRight, ShieldCheck, Sparkles, Building, Stethoscope, Utensils, Gavel, Car, Compass, ShoppingBag, Plane, Dumbbell, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

interface HeroSectionProps {
  prospect: Prospect;
  config?: Record<string, unknown>;
}

// Map segment keywords → nicho data (sem depender de availableDemos)
const detectNiche = (prospect: Prospect) => {
  const seg = (prospect.segment || '').toLowerCase();
  const demos = prospect.availableDemos || [];

  if (demos.includes('imobiliaria') || seg.includes('imobil') || seg.includes('imóvel') || seg.includes('imóveis')) {
    return {
      key: 'imobiliaria',
      label: 'Imóveis de Alto Padrão',
      icon: Home,
      heroImg: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1400&q=75&auto=format&fit=crop',
    };
  }
  if (demos.includes('clinica') || seg.includes('clínic') || seg.includes('saúde') || seg.includes('odontolog') || seg.includes('médic')) {
    return {
      key: 'clinica',
      label: 'Saúde & Bem-Estar',
      icon: Stethoscope,
      heroImg: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1400&q=75&auto=format&fit=crop',
    };
  }
  if (demos.includes('gastronomia') || seg.includes('gastronom') || seg.includes('hambúrg') || seg.includes('restaurante') || seg.includes('delivery')) {
    return {
      key: 'gastronomia',
      label: 'Gastronomia & Delivery',
      icon: Utensils,
      heroImg: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1400&q=75&auto=format&fit=crop',
    };
  }
  if (demos.includes('advocacia') || seg.includes('advocac') || seg.includes('jurídic') || seg.includes('direito')) {
    return {
      key: 'advocacia',
      label: 'Advocacia & Consultoria Jurídica',
      icon: Gavel,
      heroImg: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1400&q=75&auto=format&fit=crop',
    };
  }
  if (demos.includes('automoveis') || seg.includes('automóvel') || seg.includes('veícul') || seg.includes('concession')) {
    return {
      key: 'automoveis',
      label: 'Automóveis & Veículos Premium',
      icon: Car,
      heroImg: 'https://images.unsplash.com/photo-1617814076367-b759c7d7e738?w=1400&q=75&auto=format&fit=crop',
    };
  }
  if (demos.includes('arquitetura') || seg.includes('arquitet') || seg.includes('interior') || seg.includes('design de')) {
    return {
      key: 'arquitetura',
      label: 'Arquitetura & Design de Interiores',
      icon: Building,
      heroImg: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1400&q=75&auto=format&fit=crop',
    };
  }
  if (seg.includes('viagem') || seg.includes('viagens') || seg.includes('turism') || seg.includes('agência')) {
    return {
      key: 'viagens',
      label: 'Viagens & Experiências',
      icon: Plane,
      heroImg: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1400&q=75&auto=format&fit=crop',
    };
  }
  if (seg.includes('academi') || seg.includes('fitness') || seg.includes('cross') || seg.includes('gym')) {
    return {
      key: 'fitness',
      label: 'Academia & Performance',
      icon: Dumbbell,
      heroImg: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1400&q=75&auto=format&fit=crop',
    };
  }
  if (demos.includes('ecommerce') || seg.includes('moda') || seg.includes('calçado') || seg.includes('roupas') || seg.includes('loja')) {
    return {
      key: 'ecommerce',
      label: 'Moda & E-Commerce',
      icon: ShoppingBag,
      heroImg: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1400&q=75&auto=format&fit=crop',
    };
  }
  return {
    key: 'default',
    label: 'Negócio Digital',
    icon: Sparkles,
    heroImg: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1400&q=75&auto=format&fit=crop',
  };
};

export const HeroSection: React.FC<HeroSectionProps> = ({ prospect }) => {
  const whatsappUrl = `https://wa.me/${prospect.whatsapp}?text=${encodeURIComponent(
    `Olá YouMachine! Estou analisando a proposta para ${prospect.trade_name} e gostaria de conversar.`
  )}`;

  const niche = detectNiche(prospect);
  const NicheIcon = niche.icon;

  // Read theme from prospect — ALL light
  const primaryColor = prospect.theme?.primary_color || '#0061FF';
  const bgColor = '#FFFFFF'; // Always white — user's explicit rule
  const textColor = '#0F172A';

  // Demo links available
  const isImobiliaria = prospect.availableDemos?.includes('imobiliaria');
  const isClinica = prospect.availableDemos?.includes('clinica');
  const isGastronomia = prospect.availableDemos?.includes('gastronomia');
  const isAdvocacia = prospect.availableDemos?.includes('advocacia');
  const isAutomoveis = prospect.availableDemos?.includes('automoveis');
  const isArquitetura = prospect.availableDemos?.includes('arquitetura');
  const isEcommerce = prospect.availableDemos?.includes('ecommerce');

  return (
    <header className="relative w-full min-h-[100vh] flex items-center overflow-hidden bg-white font-sans">

      {/* ── LIGHT EDITORIAL BACKGROUND ── */}
      {/* Subtle color block top-right (Google/Stripe style) */}
      <div
        className="absolute top-0 right-0 w-1/2 h-full pointer-events-none"
        style={{ background: `linear-gradient(135deg, #F8FAFC 0%, ${primaryColor}08 100%)` }}
      />
      <div className="absolute top-0 right-0 w-px h-full bg-slate-200/60 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full pt-28 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* ── LEFT: TEXT & ACTIONS ── */}
          <div className="flex flex-col items-start text-left max-w-xl">

            {/* Eyebrow pill */}
            <div className="flex items-center gap-2 mb-6">
              <div
                className="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold border"
                style={{ color: primaryColor, borderColor: `${primaryColor}30`, backgroundColor: `${primaryColor}08` }}
              >
                <NicheIcon className="w-3.5 h-3.5" />
                Proposta Exclusiva · {prospect.city}/{prospect.state}
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-none text-slate-900 mb-4">
              Ecossistema<br />
              Digital<br />
              <span className="block mt-1" style={{ color: primaryColor }}>
                {prospect.trade_name}.
              </span>
            </h1>

            {/* Subheading */}
            <p className="text-base md:text-lg text-slate-500 font-normal leading-relaxed mb-8 max-w-lg">
              Presença digital de alta performance para{' '}
              <strong className="text-slate-900 font-semibold">{prospect.segment}</strong>.{' '}
              Domine o mercado em {prospect.city}.
            </p>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto mb-10">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <button
                  className="flex items-center gap-2 px-7 py-4 rounded-2xl font-bold text-sm text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                >
                  <MessageSquare className="w-4 h-4" />
                  Discutir Projeto
                </button>
              </a>
            </div>

            {/* Demo links — now light */}
            <div className="space-y-2 w-full">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">
                Demonstrações interativas:
              </span>
              <div className="flex flex-wrap gap-2">
                {isEcommerce && (
                  <Link to={`/${prospect.slug}/ecommerce`} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-slate-100 text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors">
                    🛍️ E-Commerce <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
                {isImobiliaria && (
                  <Link to={`/${prospect.slug}/imobiliaria`} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-slate-100 text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors">
                    🏢 Portfólio <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
                {isClinica && (
                  <Link to={`/${prospect.slug}/clinica`} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-slate-100 text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors">
                    🏥 Portal Clínica <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
                {isGastronomia && (
                  <Link to={`/${prospect.slug}/gastronomia`} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-slate-100 text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors">
                    🍔 Cardápio & Delivery <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
                {isAdvocacia && (
                  <Link to={`/${prospect.slug}/advocacia`} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-slate-100 text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors">
                    ⚖️ Portal Jurídico <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
                {isAutomoveis && (
                  <Link to={`/${prospect.slug}/automoveis`} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-slate-100 text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors">
                    🏎️ Estoque de Veículos <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
                {isArquitetura && (
                  <Link to={`/${prospect.slug}/arquitetura`} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-slate-100 text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors">
                    📐 Portfólio Editorial <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
                <Link to={`/${prospect.slug}/biolink`} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-slate-100 text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors">
                  🔗 Biolink <ArrowRight className="w-3 h-3" />
                </Link>
                <Link to={`/${prospect.slug}/app`} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-slate-100 text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors">
                  📱 App Mobile <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>

          {/* ── RIGHT: NICHE PHOTO IN CLEAN FRAME ── */}
          <div className="hidden lg:flex justify-center relative">
            <div className="relative w-full max-w-[420px]">
              {/* Main photo */}
              <div className="w-full aspect-[4/5] rounded-3xl overflow-hidden border border-slate-200/80">
                <img
                  src={niche.heroImg}
                  alt={niche.label}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Floating info card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm border border-slate-200/80 rounded-2xl p-4 flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
                >
                  <NicheIcon className="w-5 h-5" />
                </div>
                <div>
                  <strong className="text-sm font-black text-slate-900 block">{prospect.trade_name}</strong>
                  <span className="text-xs text-slate-500">{niche.label} · {prospect.city}, {prospect.state}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </header>
  );
};
